import { createProvider } from '@frontend/store';
import type { GlobalSearchFeatures } from './config';

type GlobalSearchStoreState = {
  searchTerm: string;
  debouncedSearchTerm: string;
  isExpandedView: boolean;
  searchFor: GlobalSearchFeatures;
  viewAllFeature: GlobalSearchFeatures | '';
  moduleResultsStatus: Record<Exclude<GlobalSearchFeatures, 'all'>, boolean>;
  hasResults: boolean;
};

export type GlobalSearchStoreSetters = {
  setDebouncedSearchTerm: (debouncedSearchTerm: string) => void;
  setSearchTerm: (searchTerm: string) => void;
  setModuleData: (id: Exclude<GlobalSearchFeatures, 'all'>, value: boolean) => void;
  setIsExpandedView: (isExpandedView: boolean) => void;
  setSearchFor: (globalSearchFeature: GlobalSearchFeatures) => void;
  setViewAllFeature: (id: GlobalSearchFeatures | '') => void;
  reset: () => void;
  close: () => void;
};

const stateValues: GlobalSearchStoreState = {
  searchTerm: '',
  debouncedSearchTerm: '',
  moduleResultsStatus: {
    contacts: false,
    faxes: false,
    features: false,
    settings: false,
    messaging: false,
  },
  isExpandedView: false,
  searchFor: 'all',
  viewAllFeature: '',
  hasResults: false,
};

export const closeGlobalSearchEvent = 'close-global-search';
const closeModal = new CustomEvent(closeGlobalSearchEvent);

export const {
  Provider: GlobalSearchProvider,
  useStore: useGlobalSearch,
  useNonReactiveStore: useGlobalSearchNonReactive,
} = createProvider<GlobalSearchStoreState & GlobalSearchStoreSetters>()((set, get) => ({
  ...stateValues,
  setDebouncedSearchTerm: (debouncedSearchTerm) => {
    set(() => ({ debouncedSearchTerm }));
  },
  setSearchTerm: (searchTerm) => {
    set(() => ({ searchTerm }));
  },
  setIsExpandedView: (isExpandedView) => {
    set(() => ({ isExpandedView }));
  },
  setSearchFor: (searchFor) => {
    set(() => ({ searchFor }));
  },
  setViewAllFeature: (id) => {
    set(() => ({ viewAllFeature: id }));
  },
  reset: () => {
    set({ ...stateValues });
  },
  close: () => {
    window.dispatchEvent(closeModal);
  },
  setModuleData: (id, value) => {
    if (get().moduleResultsStatus[id] === value) return;

    const updatedModuleResultsStatus = { ...get().moduleResultsStatus, [id]: value };
    const hasResults = Object.values(updatedModuleResultsStatus).some((val) => val);

    set(() => ({
      moduleResultsStatus: updatedModuleResultsStatus,
      hasResults,
    }));
  },
}));
